import React, { useCallback, useEffect, useRef, useState } from 'react';

/* ── ShareMenu ───────────────────────────────────────────────────
   A small share-icon button that opens a popover with 5 share
   targets: Copy link, Copy text, Teams, Email (Outlook), Save PDF.
   No social media targets.
   ──────────────────────────────────────────────────────────────── */

interface ShareMenuProps {
  /** Query ID, used to build the shareable URL /q/<id>. */
  queryId?: string;
  /** Full markdown response text. */
  answerText: string;
  /** Called with a short toast message after a clipboard action. */
  onToast?: (message: string) => void;
}

/* ── Icons ───────────────────────────────────────────────────── */

const ShareIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const TeamsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {/* Simplified Teams "T" shield icon */}
    <path d="M19.5 2h-15A2.5 2.5 0 0 0 2 4.5v15A2.5 2.5 0 0 0 4.5 22h15a2.5 2.5 0 0 0 2.5-2.5v-15A2.5 2.5 0 0 0 19.5 2zm-2 6h-3v8h-2V8h-3V6h8v2z" />
  </svg>
);

const EmailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PdfIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ── Helpers ──────────────────────────────────────────────────── */

/** Strip markdown to plain text for sharing. */
function stripMarkdown(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/^>\s*/gm, '')
    .trim();
}

/** Build the shareable URL for a query. */
function buildShareUrl(queryId?: string): string {
  if (queryId) {
    return `${window.location.origin}/q/${queryId}`;
  }
  return window.location.href;
}

/** Teams deep link: opens a new chat with a prefilled message. */
function buildTeamsUrl(text: string, url: string): string {
  const preview = text.slice(0, 200).replace(/\s+/g, ' ').trim();
  const message = `${preview}…\n\n${url}`;
  return `https://teams.microsoft.com/l/chat/0/0?message=${encodeURIComponent(message)}`;
}

/** mailto: for Outlook. */
function buildMailtoUrl(text: string, url: string): string {
  const subject = 'Compliance Hub Result';
  const plain = stripMarkdown(text).slice(0, 1500);
  const body = `${plain}\n\n---\nView full result: ${url}`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/* ── Component ───────────────────────────────────────────────── */

export default function ShareMenu({ queryId, answerText, onToast }: ShareMenuProps) {
  const [open, setOpen] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /* Close on outside click */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* Close on Escape */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const markCopied = useCallback((key: string, label: string) => {
    setCopiedItem(key);
    onToast?.(label);
    setTimeout(() => setCopiedItem(null), 1800);
  }, [onToast]);

  const handleCopyLink = useCallback(async () => {
    const url = buildShareUrl(queryId);
    try {
      await navigator.clipboard.writeText(url);
      markCopied('link', 'Link copied');
    } catch {
      /* silent */
    }
    setOpen(false);
  }, [queryId, markCopied]);

  const handleCopyText = useCallback(async () => {
    const plain = stripMarkdown(answerText);
    try {
      await navigator.clipboard.writeText(plain);
      markCopied('text', 'Text copied');
    } catch {
      /* silent */
    }
    setOpen(false);
  }, [answerText, markCopied]);

  const handleTeams = useCallback(() => {
    const url = buildShareUrl(queryId);
    const teamsUrl = buildTeamsUrl(answerText, url);
    window.open(teamsUrl, '_blank', 'noopener,noreferrer');
    setOpen(false);
  }, [queryId, answerText]);

  const handleEmail = useCallback(() => {
    const url = buildShareUrl(queryId);
    const mailtoUrl = buildMailtoUrl(answerText, url);
    window.location.href = mailtoUrl;
    setOpen(false);
  }, [queryId, answerText]);

  const handlePdf = useCallback(() => {
    if (queryId) {
      window.open(`/q/${queryId}?print=1`, '_blank', 'noopener,noreferrer');
    } else {
      window.print();
    }
    setOpen(false);
  }, [queryId]);

  return (
    <div
      ref={containerRef}
      className="share-menu-wrap"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Trigger button */}
      <button
        className="action-btn share-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Share options"
        title="Share this response"
      >
        <ShareIcon />
        <span style={{ marginLeft: 4 }}>Share</span>
      </button>

      {/* Popover */}
      {open && (
        <div
          className="share-menu-popover"
          role="menu"
          aria-label="Share options"
        >
          {/* Copy link */}
          <button
            className="share-menu-item"
            role="menuitem"
            onClick={handleCopyLink}
          >
            <span className="share-menu-item-icon">
              {copiedItem === 'link' ? <CheckIcon /> : <LinkIcon />}
            </span>
            <span>{copiedItem === 'link' ? 'Copied!' : 'Copy link'}</span>
          </button>

          {/* Copy text */}
          <button
            className="share-menu-item"
            role="menuitem"
            onClick={handleCopyText}
          >
            <span className="share-menu-item-icon">
              {copiedItem === 'text' ? <CheckIcon /> : <CopyIcon />}
            </span>
            <span>{copiedItem === 'text' ? 'Copied!' : 'Copy text'}</span>
          </button>

          <div className="share-menu-divider" />

          {/* Share to Teams */}
          <button
            className="share-menu-item"
            role="menuitem"
            onClick={handleTeams}
          >
            <span className="share-menu-item-icon share-menu-item-icon--teams">
              <TeamsIcon />
            </span>
            <span>Share to Teams</span>
          </button>

          {/* Email (Outlook) */}
          <button
            className="share-menu-item"
            role="menuitem"
            onClick={handleEmail}
          >
            <span className="share-menu-item-icon">
              <EmailIcon />
            </span>
            <span>Email (Outlook)</span>
          </button>

          <div className="share-menu-divider" />

          {/* Save as PDF */}
          <button
            className="share-menu-item"
            role="menuitem"
            onClick={handlePdf}
          >
            <span className="share-menu-item-icon">
              <PdfIcon />
            </span>
            <span>Save as PDF</span>
          </button>
        </div>
      )}
    </div>
  );
}
